import {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";

import type {
  CloseDrawerFn,
  OpenDrawerFn,
} from "@ui/components/navigation/Navigation/Navigation";
import NavigationContext, {
  PageRefCallbackType,
  NavigationOverrides,
} from "@ui/context/navigation-context";

interface Props {
  children?: ReactNode;
}

const NavigationProvider: FunctionComponent<PropsWithChildren<Props>> = ({
  children,
}) => {
  const subscribers = useRef<PageRefCallbackType[]>([]);
  const articleRef = useRef<HTMLDivElement | null>(null);
  const [navigationOverrides, setNavigationOverridesState] = useState<
    NavigationOverrides | undefined
  >(undefined);

  // Wrapper for setState that matches the context type signature
  const setNavigationOverrides = useCallback(
    (overrides?: NavigationOverrides) => {
      setNavigationOverridesState(overrides);
    },
    [],
  );

  const addArticleRefSubscriber = useCallback(
    (callback: PageRefCallbackType) => {
      callback(articleRef.current);

      // Call callback immediately to notify of current reference.
      subscribers.current.push(callback);

      // Unsubscribe.
      return () => {
        const i = subscribers.current.indexOf(callback);
        if (i >= 0) {
          subscribers.current.splice(i, 1);
        }
      };
    },
    [subscribers],
  );

  const articleRefCallback = (node: HTMLDivElement | null) => {
    articleRef.current = node;
    // Call all subscribers to notify of reference change.
    subscribers.current.forEach((subscriber) => subscriber(node));
  };

  let openDrawerFn: OpenDrawerFn = () => {};
  const openDrawer: OpenDrawerFn = (...args) => {
    return openDrawerFn(...args);
  };
  const setOpenDrawerFn = (fn: OpenDrawerFn) => {
    openDrawerFn = fn;
  };

  let closeDrawerFn: CloseDrawerFn = () => {};
  const closeDrawer: CloseDrawerFn = (...args) => {
    return closeDrawerFn(...args);
  };
  const setCloseDrawerFn = (fn: CloseDrawerFn) => {
    closeDrawerFn = fn;
  };

  const drawerId = `drawer_${useId()}`;

  let commentCountClickFn: () => void = () => {};
  const onCommentCountClick: () => void = () => {
    return commentCountClickFn();
  };
  const setCommentCountClickFn = (fn: () => void) => {
    commentCountClickFn = fn;
  };

  return (
    <>
      <NavigationContext.Provider
        value={{
          stickyNavTriggerClassName: "sticky-nav-trigger",
          articleRef: articleRefCallback,
          addArticleRefSubscriber,
          openDrawer,
          setOpenDrawerFn,
          closeDrawer,
          setCloseDrawerFn,
          drawerId,
          navigationOverrides,
          setNavigationOverrides,
          onCommentCountClick,
          setCommentCountClickFn,
        }}
      >
        {children}
      </NavigationContext.Provider>
    </>
  );
};

export default NavigationProvider;
